import React, { useEffect, useState } from 'react';
import {
  listLocations,
  createLocation,
  listMembers,
  inviteMember,
  updateMember,
  removeMember
} from './api';
import { useTranslation } from '../i18n';

const ROLE_PILL = {
  owner: { background: '#ecf8ee', color: '#14653a' },
  manager: { background: '#eaf2f8', color: '#2c6e9e' },
  crew_manager: { background: '#f0f2ec', color: '#5f6b58' }
};

// Owner & manager team management. role = the current user's role.
export default function Team({ role }) {
  const { t } = useTranslation();
  const isOwner = role === 'owner';

  const roleLabel = (r) => {
    if (r === 'owner') return t('team.role_owner');
    if (r === 'manager') return t('team.role_manager');
    if (r === 'crew_manager') return t('team.role_crew_manager');
    return r;
  };

  const [locations, setLocations] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showInvite, setShowInvite] = useState(false);
  const [invite, setInvite] = useState({ name: '', email: '', role: 'crew_manager', location_id: '' });
  const [saving, setSaving] = useState(false);
  const [inviteResult, setInviteResult] = useState(null); // { email, temp_password }

  const [showAddLocation, setShowAddLocation] = useState(false);
  const [newLocation, setNewLocation] = useState({ name: '', city: '' });

  const refresh = async () => {
    try {
      const [loc, mem] = await Promise.all([listLocations(), listMembers()]);
      setLocations(loc.data || []);
      setMembers(mem.data || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || t('team.err_load'));
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    setSaving(true);
    setInviteResult(null);
    try {
      const res = await inviteMember({
        name: invite.name || undefined,
        email: invite.email,
        role: invite.role,
        location_id: invite.location_id || undefined
      });
      setInviteResult({
        email: res.data.member?.email || invite.email,
        temp_password: res.data.temp_password,
        login_url: res.data.login_url
      });
      setInvite({ name: '', email: '', role: 'crew_manager', location_id: '' });
      setShowInvite(false);
      refresh();
    } catch (err) {
      setError(err.response?.data?.error || t('team.err_add_member'));
    }
    setSaving(false);
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createLocation({ name: newLocation.name, city: newLocation.city || undefined });
      setNewLocation({ name: '', city: '' });
      setShowAddLocation(false);
      refresh();
    } catch (err) {
      setError(err.response?.data?.error || t('team.err_add_location'));
    }
    setSaving(false);
  };

  const changeRole = async (m, nextRole) => {
    try {
      await updateMember(m.id, { role: nextRole });
      refresh();
    } catch (err) {
      setError(err.response?.data?.error || t('team.err_update_member'));
    }
  };

  const changeLocation = async (m, locationId) => {
    try {
      await updateMember(m.id, { location_id: locationId || null });
      refresh();
    } catch (err) {
      setError(err.response?.data?.error || t('team.err_update_member'));
    }
  };

  const handleRemove = async (m) => {
    const who = m.email || t('team.confirm_remove_default');
    if (!window.confirm(t('team.confirm_remove').replace('{who}', who))) return;
    try {
      await removeMember(m.id);
      refresh();
    } catch (err) {
      setError(err.response?.data?.error || t('team.err_remove_member'));
    }
  };

  const copy = (text) => { try { navigator.clipboard.writeText(text); } catch (e) { /* ignore */ } };

  if (loading) return <div style={styles.loading} role="status" aria-live="polite">{t('team.loading')}</div>;

  return (
    <div>
      {error && <div role="alert" style={styles.error}>{error}</div>}

      {inviteResult && (
        <div role="status" aria-live="polite" style={styles.successCard}>
          <div style={styles.successTitle}>
            <span aria-hidden="true">✓ </span>{t('team.member_added_title').replace(/^✓\s*/, '')}
          </div>
          <div style={styles.credRow}><span style={styles.credLabel}>{t('team.login_label')}</span> <code style={styles.code}>{inviteResult.login_url}</code></div>
          <div style={styles.credRow}><span style={styles.credLabel}>{t('team.email_label')}</span> <code style={styles.code}>{inviteResult.email}</code></div>
          {inviteResult.temp_password ? (
            <div style={styles.credRow}>
              <span style={styles.credLabel}>{t('team.temp_password_label')}</span> <code style={styles.code}>{inviteResult.temp_password}</code>
              <button
                type="button"
                style={styles.copyBtn}
                onClick={() => copy(inviteResult.temp_password)}
                aria-label={t('team.copy_password_aria')}
              >
                {t('team.copy')}
              </button>
            </div>
          ) : (
            <div style={styles.credNote}>{t('team.existing_account_note')}</div>
          )}
          <button type="button" style={styles.dismissBtn} onClick={() => setInviteResult(null)}>
            {t('team.done')}
          </button>
        </div>
      )}

      {/* Locations */}
      {isOwner && (
        <>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>{t('team.locations')}</h2>
            <button
              type="button"
              style={styles.addBtn}
              onClick={() => setShowAddLocation((v) => !v)}
              aria-expanded={showAddLocation}
            >
              {showAddLocation ? t('team.cancel') : t('team.add_location')}
            </button>
          </div>
          {showAddLocation && (
            <form onSubmit={handleAddLocation} style={styles.addCard}>
              <div style={styles.fieldRow}>
                <div style={styles.field}>
                  <label htmlFor="team-loc-name" style={styles.label}>
                    {t('team.location_name')} <span aria-hidden="true">*</span>
                  </label>
                  <input
                    style={styles.input}
                    id="team-loc-name"
                    required
                    aria-required="true"
                    value={newLocation.name}
                    onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                    placeholder={t('team.location_name_placeholder')}
                  />
                </div>
                <div style={styles.field}>
                  <label htmlFor="team-loc-city" style={styles.label}>{t('team.city')}</label>
                  <input
                    style={styles.input}
                    id="team-loc-city"
                    value={newLocation.city}
                    onChange={(e) => setNewLocation({ ...newLocation, city: e.target.value })}
                    placeholder={t('team.city_placeholder')}
                  />
                </div>
              </div>
              <button type="submit" disabled={saving} style={styles.saveBtn}>
                {saving ? t('team.saving') : t('team.create_location')}
              </button>
            </form>
          )}
          <div style={styles.locationRow}>
            {locations.length === 0 ? (
              <span style={styles.muted}>{t('team.no_locations')}</span>
            ) : (
              locations.map((l) => (
                <span key={l.id} style={styles.locationChip}>
                  <span aria-hidden="true">📍 </span>{l.name}{l.city ? ` · ${l.city}` : ''}
                </span>
              ))
            )}
          </div>
        </>
      )}

      {/* Members */}
      <div style={{ ...styles.sectionHeader, marginTop: isOwner ? 28 : 0 }}>
        <h2 style={styles.sectionTitle}>{t('team.members')}</h2>
        <button
          type="button"
          style={styles.addBtn}
          onClick={() => setShowInvite((v) => !v)}
          aria-expanded={showInvite}
        >
          {showInvite ? t('team.cancel') : t('team.add_member')}
        </button>
      </div>

      {showInvite && (
        <form onSubmit={handleInvite} style={styles.addCard}>
          <div style={styles.fieldRow}>
            <div style={styles.field}>
              <label htmlFor="team-invite-name" style={styles.label}>{t('team.name')}</label>
              <input
                style={styles.input}
                id="team-invite-name"
                value={invite.name}
                onChange={(e) => setInvite({ ...invite, name: e.target.value })}
                placeholder={t('team.name_placeholder')}
                autoComplete="name"
              />
            </div>
            <div style={styles.field}>
              <label htmlFor="team-invite-email" style={styles.label}>
                {t('team.email')} <span aria-hidden="true">*</span>
              </label>
              <input
                style={styles.input}
                id="team-invite-email"
                type="email"
                required
                aria-required="true"
                value={invite.email}
                onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                placeholder={t('team.email_placeholder')}
                autoComplete="email"
              />
            </div>
          </div>
          <div style={styles.fieldRow}>
            {isOwner && (
              <div style={styles.field}>
                <label htmlFor="team-invite-role" style={styles.label}>{t('team.role')}</label>
                <select
                  style={styles.input}
                  id="team-invite-role"
                  value={invite.role}
                  onChange={(e) => setInvite({ ...invite, role: e.target.value })}
                >
                  <option value="crew_manager">{t('team.role_crew_manager')}</option>
                  <option value="manager">{t('team.role_location_manager')}</option>
                </select>
              </div>
            )}
            <div style={styles.field}>
              <label htmlFor="team-invite-location" style={styles.label}>
                {t('team.location')} <span aria-hidden="true">*</span>
              </label>
              <select
                style={styles.input}
                id="team-invite-location"
                required
                aria-required="true"
                value={invite.location_id}
                onChange={(e) => setInvite({ ...invite, location_id: e.target.value })}
              >
                <option value="">{t('team.choose_location')}</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          </div>
          <button type="submit" disabled={saving} style={styles.saveBtn}>
            {saving ? t('team.adding') : t('team.add_member_btn')}
          </button>
        </form>
      )}

      <ul style={styles.list}>
        {members.map((m) => {
          const memberRoleId = `team-member-${m.id}-role`;
          const memberLocId = `team-member-${m.id}-location`;
          return (
            <li key={m.id} style={styles.member}>
              <div style={styles.avatar} aria-hidden="true">{(m.member_name || m.email || '?').slice(0, 2).toUpperCase()}</div>
              <div style={styles.memberInfo}>
                <div style={styles.memberName}>{m.member_name || m.email}</div>
                <div style={styles.memberMeta}>
                  {m.email}
                  {m.locations?.name ? (
                    <>
                      {' · '}
                      <span aria-hidden="true">📍 </span>
                      <span className="ms-sr-only">{t('team.location_pin_aria')}: </span>
                      {m.locations.name}
                    </>
                  ) : (m.role === 'owner' ? ` · ${t('team.all_locations')}` : '')}
                </div>
              </div>
              <span style={{ ...styles.rolePill, ...ROLE_PILL[m.role] }}>{roleLabel(m.role)}</span>
              {isOwner && m.role !== 'owner' && (
                <div style={styles.memberActions}>
                  <label htmlFor={memberRoleId} className="ms-sr-only">{t('team.change_role')}</label>
                  <select
                    id={memberRoleId}
                    style={styles.miniSelect}
                    value={m.role}
                    onChange={(e) => changeRole(m, e.target.value)}
                    aria-label={t('team.change_role')}
                  >
                    <option value="crew_manager">{t('team.role_crew_manager')}</option>
                    <option value="manager">{t('team.role_manager')}</option>
                  </select>
                  <label htmlFor={memberLocId} className="ms-sr-only">{t('team.change_location')}</label>
                  <select
                    id={memberLocId}
                    style={styles.miniSelect}
                    value={m.location_id || ''}
                    onChange={(e) => changeLocation(m, e.target.value)}
                    aria-label={t('team.change_location')}
                  >
                    <option value="">{t('team.no_location')}</option>
                    {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                  <button type="button" style={styles.removeBtn} onClick={() => handleRemove(m)}>
                    {t('team.remove')}
                  </button>
                </div>
              )}
              {!isOwner && m.role === 'crew_manager' && (
                <button type="button" style={styles.removeBtn} onClick={() => handleRemove(m)}>
                  {t('team.remove')}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const styles = {
  loading: { textAlign: 'center', padding: 40, color: '#5f6b58' },
  error: { background: '#FEF2F2', color: '#dc2626', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginBottom: 12 },
  muted: { fontSize: 13, color: '#5f6b58' },

  successCard: { background: '#ecf8ee', border: '1px solid #cfe9d4', borderRadius: 12, padding: 16, marginBottom: 18 },
  successTitle: { fontWeight: 800, color: '#14653a', fontSize: 14, marginBottom: 10, fontFamily: 'Manrope, sans-serif' },
  credRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' },
  credLabel: { fontSize: 12, fontWeight: 700, color: '#5f6b58', minWidth: 90 },
  code: { fontSize: 13, color: '#14653a', fontFamily: 'ui-monospace, SFMono-Regular, monospace', background: '#fff', padding: '3px 8px', borderRadius: 6, border: '1px solid #cfe9d4' },
  credNote: { fontSize: 12.5, color: '#5f6b58', marginTop: 4 },
  copyBtn: { padding: '4px 10px', border: '1px solid #cfe9d4', background: '#fff', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#14653a', cursor: 'pointer' },
  dismissBtn: { marginTop: 10, padding: '8px 16px', border: 'none', background: '#14653a', borderRadius: 8, fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer' },

  sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12 },
  sectionTitle: { fontFamily: 'Manrope, sans-serif', fontSize: 17, fontWeight: 800, color: '#1a1a1a', letterSpacing: '-0.3px', margin: 0 },
  addBtn: { padding: '8px 14px', border: 'none', background: '#14653a', borderRadius: 8, fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' },

  addCard: { background: '#fff', border: '1px solid #e3e9df', borderRadius: 14, padding: 18, marginBottom: 16 },
  fieldRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 },
  field: { display: 'flex', flexDirection: 'column' },
  label: { fontSize: 12, fontWeight: 600, color: '#5f6b58', marginBottom: 6 },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #e3e9df', borderRadius: 8, fontSize: 14, color: '#1a1a1a', background: '#f7f9f4', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' },
  saveBtn: { padding: '11px 18px', border: 'none', background: '#14653a', borderRadius: 8, fontSize: 14, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'inherit' },

  locationRow: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 },
  locationChip: { fontSize: 13, fontWeight: 600, color: '#14653a', background: '#fff', border: '1px solid #e3e9df', borderRadius: 100, padding: '7px 14px' },

  list: { display: 'flex', flexDirection: 'column', gap: 8, listStyle: 'none', padding: 0, margin: 0 },
  member: { display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #e3e9df', borderRadius: 10, padding: 12, flexWrap: 'wrap' },
  avatar: { width: 36, height: 36, borderRadius: '50%', background: '#ecf8ee', color: '#14653a', fontWeight: 800, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Manrope, sans-serif', flexShrink: 0 },
  memberInfo: { flex: 1, minWidth: 140 },
  memberName: { fontSize: 14, fontWeight: 700, color: '#1a1a1a' },
  memberMeta: { fontSize: 12, color: '#5f6b58', marginTop: 2 },
  rolePill: { fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 100, textTransform: 'uppercase', letterSpacing: '0.02em', flexShrink: 0 },
  memberActions: { display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, flexWrap: 'wrap' },
  miniSelect: { padding: '6px 8px', border: '1px solid #e3e9df', borderRadius: 7, fontSize: 12, color: '#1a1a1a', background: '#fff', fontFamily: 'inherit' },
  removeBtn: { padding: '6px 12px', border: '1px solid #f2c9c9', background: '#fff', borderRadius: 7, fontSize: 12, fontWeight: 600, color: '#b91c1c', cursor: 'pointer', fontFamily: 'inherit' }
};
